import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Clock,
	FileText,
	Percent,
	Plus,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	apiResponseToBuilderFormat,
	builderFormToCreateRequest,
} from "@/api/mappers/scenarioAdjustmentMapper";
import * as adjApi from "@/api/services/scenarioAdjustmentService";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApp } from "@/contexts/AppContext";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { useScenarios } from "@/hooks/useScenarios";
import { useTerminology } from "@/hooks/useTerminology";
import { useVisibleScenarios } from "@/hooks/useVisibleScenarios";
import { normalizeAdjustment } from "@/lib/normalizeAdjustment";
import { calculateScenarioMetrics } from "@/lib/scenarioEngine";
import type { ScenarioMetrics } from "@/types/scenarios";
import { ScenarioTransactionsTable } from "../ScenarioTransactionsTable";
import { AdjustmentCard } from "./AdjustmentCard";
import { CollapsibleGroup } from "./CollapsibleGroup";
import {
	AddScenarioTransactionForm as AddScenarioCostTransactionForm,
	AdjustCostsByPercentForm,
	AdjustSpecificTransactionForm as AdjustSpecificCostTransactionForm,
} from "./CostForms";
import {
	AddScenarioTransactionForm,
	AdjustRevenueByPercentForm,
	AdjustSpecificTransactionForm,
} from "./RevenueForms";
import { ScenarioImpactPreview } from "./ScenarioImpactPreview";

interface ScenarioBuilderProps {
	scenarioId: string;
	onAdjustmentCountChange?: (count: number) => void;
	onLiveMetricsChange?: (metrics: ScenarioMetrics | null) => void;
	readOnly?: boolean;
	/**
	 * External refresh counter (e.g. the header Refresh button re-materialised
	 * the scenario). Merged with the builder's own adjustment-change counter so
	 * the scenario transactions table refetches its latest data.
	 */
	refreshSignal?: number;
}

type RevenueFormType =
	| "adjust-percent"
	| "adjust-transaction"
	| "add-scenario-transaction"
	| null;
type CostFormType =
	| "adjust-percent"
	| "adjust-transaction"
	| "add-scenario-transaction"
	| null;

export const ScenarioBuilder: React.FC<ScenarioBuilderProps> = ({
	scenarioId,
	onAdjustmentCountChange,
	onLiveMetricsChange,
	readOnly = false,
	refreshSignal,
}) => {
	const { formatCurrency } = useFormatting();
	const { mode } = useApp();
	const queryClient = useQueryClient();
	// Bumped after every live adjustment mutation so the scenario transactions
	// table refetches; merged with the external refresh signal below.
	const [adjustmentRefreshKey, setAdjustmentRefreshKey] = useState(0);
	const scenarios = useVisibleScenarios();
	const scenariosRef = useRef(scenarios);
	scenariosRef.current = scenarios;
	const { updateScenario, createVersion } = useScenarios();
	const { getScenarioTerm } = useTerminology();
	const isLive = mode === "live";

	const loadedScenarioId = useRef<string>("");

	// Base forecast — from shared context for engine calculations
	const { baseForecast } = useBaseForecast();

	// Compute live forecast totals for denormalizeForEdit
	const liveForecastInflows = baseForecast.reduce(
		(sum, dp) => sum + dp.incoming,
		0,
	);
	const liveForecastOutflows = baseForecast.reduce(
		(sum, dp) => sum + dp.outgoing,
		0,
	);

	// Scenario chart "View Transactions" drill-down — filters the scenario
	// transactions table below the chart to the selected forecast day.
	const [txnDateFilter, setTxnDateFilter] = useState<Date | null>(null);

	// Revenue state
	const [revenueAdjustments, setRevenueAdjustments] = useState<any[]>([]);
	const [activeRevenueForm, setActiveRevenueForm] =
		useState<RevenueFormType>(null);
	const [liveRevenuePreview, setLiveRevenuePreview] = useState<any>(null);
	const [editingRevenueAdjustment, setEditingRevenueAdjustment] = useState<
		any | null
	>(null);

	// Pending delete state (items stay in list until timeout fires)
	const [pendingDeleteItem, setPendingDeleteItem] = useState<{
		id: number;
		name: string;
		type: "revenue" | "cost";
	} | null>(null);

	// Cost state
	const [costAdjustments, setCostAdjustments] = useState<any[]>([]);
	const [activeCostForm, setActiveCostForm] = useState<CostFormType>(null);
	const [liveCostPreview, setLiveCostPreview] = useState<any>(null);
	const [editingCostAdjustment, setEditingCostAdjustment] = useState<
		any | null
	>(null);

	// Transaction ids already targeted by a "Adjust Specific Transaction" so the
	// picker can hide them — a transaction may only be adjusted once per scenario.
	const collectUsedTransactionIds = (adjustments: any[]): string[] =>
		adjustments
			.map((a) => a?.details?.transactionId ?? a?.transactionId)
			.filter((id): id is string | number => id !== undefined && id !== null && id !== "")
			.map((id) => String(id));

	const usedRevenueTransactionIds = useMemo(
		() => collectUsedTransactionIds(revenueAdjustments),
		[revenueAdjustments],
	);
	const usedCostTransactionIds = useMemo(
		() => collectUsedTransactionIds(costAdjustments),
		[costAdjustments],
	);

	// Normalize live adjustments into ScenarioChange[] for the engine
	const normalizedChanges = useMemo(() => {
		const allAdjustments = [...revenueAdjustments, ...costAdjustments];
		if (allAdjustments.length === 0) return [];
		return allAdjustments.map(normalizeAdjustment);
	}, [revenueAdjustments, costAdjustments]);

	// Compute and emit live metrics whenever normalized changes update
	useEffect(() => {
		if (!onLiveMetricsChange) return;
		if (normalizedChanges.length === 0) {
			onLiveMetricsChange(null);
			return;
		}
		const metrics = calculateScenarioMetrics(baseForecast, normalizedChanges);
		onLiveMetricsChange(metrics);
	}, [normalizedChanges, baseForecast, onLiveMetricsChange]);

	// ── Live mode: fetch adjustments from GraphQL (includes netCashImpact) ───
	const numericScenarioId = Number(scenarioId);
	const { data: liveAdjustmentsGql } = useQuery({
		queryKey: ["scenarioAdjustmentsGraphql", numericScenarioId],
		queryFn: () => adjApi.getScenarioAdjustmentsGraphql(numericScenarioId, 200),
		enabled: isLive && !!scenarioId,
		staleTime: 30_000,
	});

	// Keep the scenario gallery/detail lists in sync after a live adjustment
	// mutation so persisted counts (e.g. adjustmentCount) and the gallery cards
	// reflect the change immediately rather than showing stale data.
	const invalidateScenarioListQueries = () => {
		queryClient.invalidateQueries({ queryKey: ["scenarios"] });
		queryClient.invalidateQueries({ queryKey: ["scenarioViews"] });
		queryClient.invalidateQueries({ queryKey: ["scenarioViewsCount"] });
	};

	// Load existing changes from scenario — only once per scenarioId
	// Use scenariosRef.current so this effect does NOT re-run when scenarios array reference changes
	useEffect(() => {
		if (isLive) return; // live mode uses its own query
		if (loadedScenarioId.current === scenarioId) return;
		loadedScenarioId.current = scenarioId;

		const scenario = scenariosRef.current.find((s) => s.id === scenarioId);
		if (scenario && scenario.changes) {
			// Filter revenue adjustments
			const revenueChanges = scenario.changes.filter(
				(change) =>
					change.type === "add-revenue" ||
					change.type === "adjust-revenue" ||
					change.type === "change-revenue" ||
					change.type === "win-customer",
			);

			// Filter cost adjustments
			const costChanges = scenario.changes.filter(
				(change) =>
					change.type === "change-costs" ||
					change.type === "delay-payment" ||
					change.type === "add-cost" ||
					change.type === "adjust-cost" ||
					change.type === "hire-employees" ||
					change.type === "capital-purchase",
			);

			setRevenueAdjustments(revenueChanges);
			setCostAdjustments(costChanges);
		}
	}, [scenarioId, isLive]);

	// Live mode: populate builder state from GraphQL response (with netCashImpact)
	useEffect(() => {
		if (!isLive || !liveAdjustmentsGql) return;
		const nodes = liveAdjustmentsGql.edges.map((e) => e.node);
		const all = nodes.map((node) => {
			const apiShape = {
				id: Number(node.id),
				scenarioId: node.scenarioId,
				adjustmentType: node.adjustmentType,
				adjustmentDirection: node.adjustmentDirection,
				startDate: node.startDate,
				endDate: node.endDate,
				frequency: node.frequency,
				confidenceLevel: node.confidenceLevel,
				description: node.description,
				additionalComments: node.additionalComments,
				adjustmentValuePercentage: node.adjustmentValuePercentage,
				adjustedValue: node.adjustedValue,
				adjustedDueDate: node.adjustedDueDate,
				linkedTransactionId: node.linkedTransactionId,
				transactionType: node.transactionType,
				monthEndHandling: node.monthEndHandling,
				linkedAccountName: node.linkedAccountName,
				linkedTransactionSource: node.linkedTransactionSource,
				linkedTransactionAmount: node.linkedTransactionAmount,
				linkedTransactionDueDate: node.linkedTransactionDueDate,
				linkedTransactionStatus: node.linkedTransactionStatus,
				createdByUserId: node.createdByUserId,
				createdByUserName: node.createdByUserName,
				dateCreated: node.dateCreated,
				dateModified: node.dateModified,
			};
			return apiResponseToBuilderFormat(apiShape as any, node.netCashImpact);
		});
		setRevenueAdjustments(
			all.filter(
				(a: { type: string }) =>
					a.type === "change-revenue" ||
					a.type === "adjust-revenue" ||
					a.type === "add-revenue",
			),
		);
		setCostAdjustments(
			all.filter(
				(a: { type: string }) =>
					a.type === "change-costs" ||
					a.type === "adjust-cost" ||
					a.type === "add-cost",
			),
		);
	}, [isLive, liveAdjustmentsGql]);

	// Calculate total adjustment count and notify parent
	useEffect(() => {
		const totalCount = revenueAdjustments.length + costAdjustments.length;

		if (onAdjustmentCountChange) {
			onAdjustmentCountChange(totalCount);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [revenueAdjustments, costAdjustments]);

	/**
	 * Convert a canonical (API) adjustment back into the flat shape that
	 * the builder forms expect, so every field pre-populates on edit.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const denormalizeForEdit = (adj: any): any => {
		const details = adj.details || {};
		switch (adj.type) {
			// Revenue: % adjustment
			case "change-revenue":
				return {
					...adj,
					type: "adjust-revenue-percent",
					adjustmentPercent: details.percentage ?? adj.adjustmentPercent ?? 0,
					entity: details.entity ?? "All Entities",
					effectiveDate: adj.effectiveDate
						? new Date(adj.effectiveDate)
						: adj.date
							? new Date(adj.date)
							: undefined,
					endDate: adj.endDate ? new Date(adj.endDate) : undefined,
					frequency: adj.frequency ?? details.frequency ?? "monthly",
					monthlyDateMode: adj.monthlyDateMode ?? "same-date",
					reason: adj.reason ?? adj.description ?? "",
					confidence: adj.confidence ?? details.confidence ?? "probable",
					adjustmentAmount: adj.amount ?? 0,
					monthlyImpact: details.monthlyAmount ?? 0,
					estimatedCurrentRevenue: isLive ? liveForecastInflows : 1200000,
				};
			// Cost: % adjustment
			case "change-costs":
				return {
					...adj,
					type: "adjust-costs-percent",
					adjustmentPercent: details.percentage ?? adj.adjustmentPercent ?? 0,
					category: adj.category ?? details.category ?? "",
					entity: details.entity ?? "All Entities",
					effectiveDate: adj.effectiveDate
						? new Date(adj.effectiveDate)
						: adj.date
							? new Date(adj.date)
							: undefined,
					endDate: adj.endDate ? new Date(adj.endDate) : undefined,
					frequency: adj.frequency ?? details.frequency ?? "monthly",
					monthlyDateMode: adj.monthlyDateMode ?? "same-date",
					reason: adj.reason ?? adj.description ?? "",
					confidence: adj.confidence ?? details.confidence ?? "probable",
					monthlyImpact: details.monthlySavings
						? -Math.abs(details.monthlySavings)
						: details.monthlyIncrease
							? Math.abs(details.monthlyIncrease)
							: 0,
					annualImpact: adj.amount ?? 0,
				};
			// Revenue / Cost: adjust specific transaction
			case "adjust-revenue":
			case "adjust-cost":
				return {
					...adj,
					type: "adjust-transaction",
					transactionId: details.transactionId ?? "",
					adjustedValue: adj.amount ?? 0,
					newDueDate: adj.newDueDate ? new Date(adj.newDueDate) : undefined,
					dateChangeDays: adj.delayDays ?? 0,
					valueChange: details.valueChange ?? 0,
					reason: adj.description ?? "",
					confidence: details.confidence ?? "probable",
				};
			// Revenue: add scenario transaction
			case "add-revenue":
				return {
					...adj,
					type: "add-scenario-transaction",
					transactionType: adj.transactionType ?? details.transactionType ?? "",
					date: adj.date ? new Date(adj.date) : undefined,
					endDate: adj.endDate ? new Date(adj.endDate) : undefined,
					amount: adj.amount ?? 0,
					description: adj.name ?? "",
					reason: adj.description ?? "",
					recurrence: adj.recurrence ?? adj.frequency ?? "one-off",
					monthlyDateMode: adj.monthlyDateMode ?? "same-date",
					confidence: adj.confidence ?? details.confidence ?? "probable",
				};
			// Cost: add scenario transaction
			case "add-cost":
				return {
					...adj,
					type: "add-scenario-transaction",
					transactionType: adj.transactionType ?? details.transactionType ?? "",
					expectedDate: adj.date ? new Date(adj.date) : undefined,
					endDate: adj.endDate ? new Date(adj.endDate) : undefined,
					amount: adj.amount ?? 0,
					description: adj.name ?? "",
					businessContext: adj.description ?? "",
					recurrence: adj.recurrence ?? adj.frequency ?? "one-off",
					monthlyDateMode: adj.monthlyDateMode ?? "same-date",
					confidence: adj.confidence ?? details.confidence ?? "probable",
				};
			default:
				return adj;
		}
	};

	// Revenue handlers
	const handleSaveRevenueAdjustment = async (data: any) => {
		if (isLive) {
			const request = builderFormToCreateRequest(data, "inflow");
			if (editingRevenueAdjustment) {
				await adjApi.updateScenarioAdjustment(
					numericScenarioId,
					editingRevenueAdjustment.id,
					request,
				);
				setEditingRevenueAdjustment(null);
			} else {
				await adjApi.createScenarioAdjustment(numericScenarioId, request);
			}
			setActiveRevenueForm(null);
			setLiveRevenuePreview(null);
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustments", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustmentsGraphql", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustmentTotals", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenario-forecast", numericScenarioId],
			});
			invalidateScenarioListQueries();
			setAdjustmentRefreshKey((k) => k + 1);
			return;
		}

		let newRevenue: any[];
		let isEdit = false;
		if (editingRevenueAdjustment) {
			isEdit = true;
			newRevenue = revenueAdjustments.map((adj) =>
				adj.id === editingRevenueAdjustment.id
					? { ...data, id: editingRevenueAdjustment.id }
					: adj,
			);
			setRevenueAdjustments(newRevenue);
			setEditingRevenueAdjustment(null);
		} else {
			newRevenue = [...revenueAdjustments, { ...data, id: Date.now() }];
			setRevenueAdjustments(newRevenue);
		}
		setActiveRevenueForm(null);
		setLiveRevenuePreview(null);

		const allChanges = [...newRevenue, ...costAdjustments].map(
			normalizeAdjustment,
		);
		await updateScenario(scenarioId, { changes: allChanges });
		createVersion(
			scenarioId,
			isEdit
				? `Updated revenue adjustment: ${data.name || "Untitled"}`
				: `Added revenue adjustment: ${data.name || "Untitled"}`,
		);
	};

	const handleCancelRevenueForm = () => {
		setActiveRevenueForm(null);
		setEditingRevenueAdjustment(null);
		setLiveRevenuePreview(null);
	};

	const handleRemoveRevenueAdjustment = (id: number) => {
		const removed = revenueAdjustments.find((adj) => adj.id === id);
		if (!removed) return;
		setPendingDeleteItem({
			id,
			name: removed.name || "Adjustment",
			type: "revenue",
		});
	};

	const confirmDelete = async () => {
		if (!pendingDeleteItem) return;
		const { id, type } = pendingDeleteItem;

		if (isLive) {
			await adjApi.deleteScenarioAdjustment(numericScenarioId, id);
			setPendingDeleteItem(null);
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustments", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustmentsGraphql", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustmentTotals", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenario-forecast", numericScenarioId],
			});
			invalidateScenarioListQueries();
			setAdjustmentRefreshKey((k) => k + 1);
			return;
		}

		if (type === "revenue") {
			const newRevenue = revenueAdjustments.filter((adj) => adj.id !== id);
			setRevenueAdjustments(newRevenue);
			const allChanges = [...newRevenue, ...costAdjustments].map(
				normalizeAdjustment,
			);
			await updateScenario(scenarioId, { changes: allChanges });
			createVersion(scenarioId, "Removed revenue adjustment");
		} else {
			const newCosts = costAdjustments.filter((adj) => adj.id !== id);
			setCostAdjustments(newCosts);
			const allChanges = [...revenueAdjustments, ...newCosts].map(
				normalizeAdjustment,
			);
			await updateScenario(scenarioId, { changes: allChanges });
			createVersion(scenarioId, "Removed cost adjustment");
		}
		setPendingDeleteItem(null);
	};

	const handleEditRevenueAdjustment = (adjustment: any) => {
		const editData = denormalizeForEdit(adjustment);
		setEditingRevenueAdjustment(editData);
		// Determine which form to open based on original adjustment type
		if (
			adjustment.type === "adjust-revenue-percent" ||
			adjustment.type === "change-revenue"
		) {
			setActiveRevenueForm("adjust-percent");
		} else if (
			adjustment.type === "adjust-transaction" ||
			adjustment.type === "adjust-revenue"
		) {
			setActiveRevenueForm("adjust-transaction");
		} else if (
			adjustment.type === "add-scenario-transaction" ||
			adjustment.type === "add-revenue"
		) {
			setActiveRevenueForm("add-scenario-transaction");
		}
	};

	// Cost handlers
	const handleSaveCostAdjustment = async (data: any) => {
		if (isLive) {
			const request = builderFormToCreateRequest(data, "outflow");
			if (editingCostAdjustment) {
				await adjApi.updateScenarioAdjustment(
					numericScenarioId,
					editingCostAdjustment.id,
					request,
				);
				setEditingCostAdjustment(null);
			} else {
				await adjApi.createScenarioAdjustment(numericScenarioId, request);
			}
			setActiveCostForm(null);
			setLiveCostPreview(null);
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustments", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustmentsGraphql", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenarioAdjustmentTotals", numericScenarioId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scenario-forecast", numericScenarioId],
			});
			invalidateScenarioListQueries();
			setAdjustmentRefreshKey((k) => k + 1);
			return;
		}

		let newCosts: any[];
		let isEdit = false;
		if (editingCostAdjustment) {
			isEdit = true;
			newCosts = costAdjustments.map((adj) =>
				adj.id === editingCostAdjustment.id
					? { ...data, id: editingCostAdjustment.id }
					: adj,
			);
			setCostAdjustments(newCosts);
			setEditingCostAdjustment(null);
		} else {
			newCosts = [...costAdjustments, { ...data, id: Date.now() }];
			setCostAdjustments(newCosts);
		}
		setActiveCostForm(null);
		setLiveCostPreview(null);

		const allChanges = [...revenueAdjustments, ...newCosts].map(
			normalizeAdjustment,
		);
		await updateScenario(scenarioId, { changes: allChanges });
		createVersion(
			scenarioId,
			isEdit
				? `Updated cost adjustment: ${data.name || "Untitled"}`
				: `Added cost adjustment: ${data.name || "Untitled"}`,
		);
	};

	const handleCancelCostForm = () => {
		setActiveCostForm(null);
		setEditingCostAdjustment(null);
		setLiveCostPreview(null);
	};

	const handleRemoveCostAdjustment = (id: number) => {
		const removed = costAdjustments.find((adj) => adj.id === id);
		if (!removed) return;
		setPendingDeleteItem({
			id,
			name: removed.name || "Adjustment",
			type: "cost",
		});
	};

	const handleEditCostAdjustment = (adjustment: any) => {
		const editData = denormalizeForEdit(adjustment);
		setEditingCostAdjustment(editData);
		// Determine which form to open based on original adjustment type
		if (
			adjustment.type === "adjust-costs-percent" ||
			adjustment.type === "change-costs"
		) {
			setActiveCostForm("adjust-percent");
		} else if (
			adjustment.type === "adjust-transaction" ||
			adjustment.type === "adjust-cost"
		) {
			setActiveCostForm("adjust-transaction");
		} else if (
			adjustment.type === "add-scenario-transaction" ||
			adjustment.type === "add-cost"
		) {
			setActiveCostForm("add-scenario-transaction");
		}
	};

	// Calculate revenue summary
	// In live mode, prefer netCashImpact from GraphQL when available;
	// otherwise fall back to the local form-level calculations (demo mode / new adjustments).
	const revenueSummary = revenueAdjustments.reduce((acc, adj) => {
		if (isLive && adj.netCashImpact != null) {
			return acc + adj.netCashImpact;
		}
		if (adj.type === "adjust-revenue-percent") {
			return acc + adj.monthlyImpact;
		} else if (adj.type === "change-revenue") {
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 12;
			return acc + monthly;
		} else if (
			adj.type === "add-scenario-transaction" ||
			adj.type === "add-revenue"
		) {
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 12;
			return acc + monthly;
		} else if (
			adj.type === "adjust-transaction" ||
			adj.type === "adjust-revenue"
		) {
			const valueChange = adj.valueChange ?? adj.details?.valueChange ?? 0;
			return acc + valueChange / 12;
		} else if (adj.type === "win-customer") {
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 3;
			return acc + monthly;
		}
		return acc;
	}, 0);

	// In live mode with netCashImpact, show total impact; otherwise show monthly breakdown
	const hasLiveNetCashImpact =
		isLive &&
		revenueAdjustments.some(
			(a: { netCashImpact?: number | null }) => a.netCashImpact != null,
		);

	// Calculate total period impact for revenue adjustments (using endDate where available)
	const revenuePeriodTotal = revenueAdjustments.reduce((acc, adj) => {
		const startDate = adj.details?.effectiveDate
			? new Date(adj.details.effectiveDate)
			: new Date();
		const endDate = adj.details?.endDate ? new Date(adj.details.endDate) : null;
		if (!endDate) return acc;
		const months = Math.max(
			1,
			(endDate.getFullYear() - startDate.getFullYear()) * 12 +
				(endDate.getMonth() - startDate.getMonth()),
		);
		const monthly =
			adj.details?.monthlyAmount ?? adj.monthlyImpact ?? adj.amount / 12;
		return acc + monthly * months;
	}, 0);

	const revenueHasEndDates = revenueAdjustments.some(
		(adj) => adj.details?.endDate,
	);
	const revenueSummaryText =
		revenueAdjustments.length > 0
			? hasLiveNetCashImpact
				? `Net impact: ${revenueSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(revenueSummary)), "full")}`
				: revenueHasEndDates
					? `Net impact: ${revenueSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(revenueSummary)), "full")}/month · Total: ${revenuePeriodTotal >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(revenuePeriodTotal)), "full")}`
					: `Net impact: ${revenueSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(revenueSummary)), "full")}/month`
			: undefined;

	// Calculate cost summary
	// In live mode, prefer netCashImpact from GraphQL when available;
	// otherwise fall back to the local form-level calculations (demo mode / new adjustments).
	const costSummary = costAdjustments.reduce((acc, adj) => {
		if (isLive && adj.netCashImpact != null) {
			return acc + adj.netCashImpact;
		}
		if (adj.type === "adjust-costs-percent") {
			return acc + adj.monthlyImpact;
		} else if (adj.type === "change-costs") {
			const monthlySavings = adj.details?.monthlySavings;
			const monthlyIncrease = adj.details?.monthlyIncrease;
			if (monthlySavings !== undefined || monthlyIncrease !== undefined) {
				if (adj.details?.changeType === "reduction") {
					return acc + Number(monthlySavings ?? 0);
				}
				return acc - Number(monthlyIncrease ?? 0);
			}
			return acc + (adj.amount ?? 0);
		} else if (
			adj.type === "add-scenario-transaction" ||
			adj.type === "add-cost"
		) {
			const monthly = adj.details?.monthlyAmount ?? adj.amount / 12;
			return acc - monthly;
		} else if (
			adj.type === "adjust-transaction" ||
			adj.type === "adjust-cost"
		) {
			const valueChange = adj.valueChange ?? adj.details?.valueChange ?? 0;
			return acc + valueChange / 12;
		} else if (adj.type === "hire-employees") {
			const monthly = adj.details?.monthlyCost ?? Math.abs(adj.amount) / 3;
			return acc - monthly;
		} else if (adj.type === "capital-purchase") {
			const monthly = Math.abs(adj.amount) / 3;
			return acc - monthly;
		}
		return acc;
	}, 0);

	const hasLiveCostNetCashImpact =
		isLive &&
		costAdjustments.some(
			(a: { netCashImpact?: number | null }) => a.netCashImpact != null,
		);

	// Separate recurring monthly impact from one-off raw amounts
	const costOneOffTotal = costAdjustments
		.filter(
			(adj) =>
				(adj.type === "change-costs" &&
					adj.details?.monthlySavings === undefined &&
					adj.details?.monthlyIncrease === undefined) ||
				adj.type === "capital-purchase",
		)
		.reduce((acc: number, adj) => acc + Math.abs(adj.amount ?? 0), 0);
	const costRecurringSummary = costSummary + costOneOffTotal;

	// Calculate total period impact for cost adjustments (using endDate where available)
	const costPeriodTotal = costAdjustments.reduce((acc, adj) => {
		const startDate = adj.details?.effectiveDate
			? new Date(adj.details.effectiveDate)
			: new Date();
		const endDate = adj.details?.endDate ? new Date(adj.details.endDate) : null;
		if (!endDate) return acc;
		const months = Math.max(
			1,
			(endDate.getFullYear() - startDate.getFullYear()) * 12 +
				(endDate.getMonth() - startDate.getMonth()),
		);
		const monthly =
			adj.details?.monthlyAmount ?? adj.monthlyImpact ?? adj.amount / 12;
		return acc + monthly * months;
	}, 0);

	const costHasEndDates = costAdjustments.some((adj) => adj.details?.endDate);

	const costSummaryText =
		costAdjustments.length > 0
			? hasLiveCostNetCashImpact
				? `Net impact: ${costSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costSummary)), "full")}`
				: costOneOffTotal > 0 && costRecurringSummary === 0
					? `One-off cost: -${formatCurrency(Math.round(costOneOffTotal), "full")}`
					: costOneOffTotal > 0
						? `Net impact: ${costRecurringSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costRecurringSummary)), "full")}/month · One-off: -${formatCurrency(Math.round(costOneOffTotal), "full")}`
						: costHasEndDates
							? `Net impact: ${costSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costSummary)), "full")}/month · Total: ${costPeriodTotal >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costPeriodTotal)), "full")}`
							: `Net impact: ${costSummary >= 0 ? "+" : "-"}${formatCurrency(Math.round(Math.abs(costSummary)), "full")}/month`
			: undefined;

	// Render revenue add button with dropdown
	const renderRevenueAddButton = () => {
		if (activeRevenueForm) return null;
		if (readOnly) return null;

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" className="w-full border-dashed">
						<Plus className="w-4 h-4 mr-2" />
						Add Revenue Adjustment
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="w-56">
					<DropdownMenuItem
						onClick={() => setActiveRevenueForm("adjust-percent")}
					>
						<Percent className="w-4 h-4 mr-2" />
						Adjust Revenue by %
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveRevenueForm("adjust-transaction")}
					>
						<Clock className="w-4 h-4 mr-2" />
						Adjust Specific Transaction
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveRevenueForm("add-scenario-transaction")}
					>
						<FileText className="w-4 h-4 mr-2" />
						Add {getScenarioTerm(true, false)} Transaction
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	// Render cost add button with dropdown
	const renderCostAddButton = () => {
		if (activeCostForm) return null;
		if (readOnly) return null;

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" className="w-full border-dashed">
						<Plus className="w-4 h-4 mr-2" />
						Add Cost Adjustment
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="w-56">
					<DropdownMenuItem onClick={() => setActiveCostForm("adjust-percent")}>
						<Percent className="w-4 h-4 mr-2" />
						Adjust Costs by %
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveCostForm("adjust-transaction")}
					>
						<FileText className="w-4 h-4 mr-2" />
						Adjust Specific Transaction
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setActiveCostForm("add-scenario-transaction")}
					>
						<Plus className="w-4 h-4 mr-2" />
						Add {getScenarioTerm(true, false)} Transaction
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	return (
		<>
			<div className="flex flex-col gap-6">
				{/* BUILDER GROUPS */}
				<div className="space-y-4">
					{/* Header */}
					<div className="mb-6">
						<h2 className="text-lg font-semibold text-gray-900 mb-1">
							Scenario Builder
						</h2>
						<p className="text-sm text-gray-600">
							{readOnly
								? "View the adjustments applied to this scenario. You have read-only access."
								: "Build your scenario by adding adjustments below. Each group can contain multiple changes that will be applied to your forecast."}
						</p>
					</div>

					{/* Collapsible Groups */}
					<div className="space-y-3">
						{/* 1. Revenue Adjustments */}
						<CollapsibleGroup
							icon={<TrendingUp className="w-5 h-5" />}
							title="Revenue Adjustments"
							count={revenueAdjustments.length}
							summary={revenueSummaryText}
							isEmpty={revenueAdjustments.length === 0 && !activeRevenueForm}
							emptyLabel="No revenue adjustments"
							customAddButton={renderRevenueAddButton()}
						>
							{/* Render adjustments with inline edit form below the card being edited */}
							{revenueAdjustments.map((adjustment) => (
								<React.Fragment key={adjustment.id}>
									{/* Hide the card when it is being edited */}
									{editingRevenueAdjustment?.id !== adjustment.id && (
										<AdjustmentCard
											adjustment={adjustment}
											onEdit={
												readOnly
													? undefined
													: () => handleEditRevenueAdjustment(adjustment)
											}
											onRemove={
												readOnly
													? undefined
													: () => handleRemoveRevenueAdjustment(adjustment.id)
											}
										/>
									)}
									{/* Render edit form inline directly below (replacing) the card being edited */}
									{editingRevenueAdjustment?.id === adjustment.id &&
										activeRevenueForm === "adjust-percent" && (
											<AdjustRevenueByPercentForm
												key={`edit-${adjustment.id}`}
												onSave={handleSaveRevenueAdjustment}
												onCancel={handleCancelRevenueForm}
												onLivePreview={setLiveRevenuePreview}
												initialData={editingRevenueAdjustment}
											/>
										)}
									{editingRevenueAdjustment?.id === adjustment.id &&
										activeRevenueForm === "adjust-transaction" && (
											<AdjustSpecificTransactionForm
												key={`edit-${adjustment.id}`}
												onSave={handleSaveRevenueAdjustment}
												onCancel={handleCancelRevenueForm}
												onLivePreview={setLiveRevenuePreview}
												initialData={editingRevenueAdjustment}
												usedTransactionIds={usedRevenueTransactionIds}
											/>
										)}
									{editingRevenueAdjustment?.id === adjustment.id &&
										activeRevenueForm === "add-scenario-transaction" && (
											<AddScenarioTransactionForm
												key={`edit-${adjustment.id}`}
												onSave={handleSaveRevenueAdjustment}
												onCancel={handleCancelRevenueForm}
												onLivePreview={setLiveRevenuePreview}
												initialData={editingRevenueAdjustment}
											/>
										)}
								</React.Fragment>
							))}

							{/* Render add form (only when not editing an existing adjustment) */}
							{!editingRevenueAdjustment &&
								activeRevenueForm === "adjust-percent" && (
									<AdjustRevenueByPercentForm
										onSave={handleSaveRevenueAdjustment}
										onCancel={handleCancelRevenueForm}
										onLivePreview={setLiveRevenuePreview}
									/>
								)}
							{!editingRevenueAdjustment &&
								activeRevenueForm === "adjust-transaction" && (
									<AdjustSpecificTransactionForm
										onSave={handleSaveRevenueAdjustment}
										onCancel={handleCancelRevenueForm}
										onLivePreview={setLiveRevenuePreview}
										usedTransactionIds={usedRevenueTransactionIds}
									/>
								)}
							{!editingRevenueAdjustment &&
								activeRevenueForm === "add-scenario-transaction" && (
									<AddScenarioTransactionForm
										onSave={handleSaveRevenueAdjustment}
										onCancel={handleCancelRevenueForm}
										onLivePreview={setLiveRevenuePreview}
									/>
								)}
						</CollapsibleGroup>

						{/* 2. Cost Adjustments */}
						<CollapsibleGroup
							icon={<TrendingDown className="w-5 h-5" />}
							title="Cost Adjustments"
							count={costAdjustments.length}
							summary={costSummaryText}
							isEmpty={costAdjustments.length === 0 && !activeCostForm}
							emptyLabel="No cost adjustments"
							customAddButton={renderCostAddButton()}
						>
							{/* Render adjustments with inline edit form below the card being edited */}
							{costAdjustments.map((adjustment) => (
								<React.Fragment key={adjustment.id}>
									{/* Hide the card when it is being edited */}
									{editingCostAdjustment?.id !== adjustment.id && (
										<AdjustmentCard
											adjustment={adjustment}
											onEdit={
												readOnly
													? undefined
													: () => handleEditCostAdjustment(adjustment)
											}
											onRemove={
												readOnly
													? undefined
													: () => handleRemoveCostAdjustment(adjustment.id)
											}
										/>
									)}
									{/* Render edit form inline directly below (replacing) the card being edited */}
									{editingCostAdjustment?.id === adjustment.id &&
										activeCostForm === "adjust-percent" && (
											<AdjustCostsByPercentForm
												key={`edit-${adjustment.id}`}
												onSave={handleSaveCostAdjustment}
												onCancel={handleCancelCostForm}
												onLivePreview={setLiveCostPreview}
												initialData={editingCostAdjustment}
											/>
										)}
									{editingCostAdjustment?.id === adjustment.id &&
										activeCostForm === "adjust-transaction" && (
											<AdjustSpecificCostTransactionForm
												key={`edit-${adjustment.id}`}
												onSave={handleSaveCostAdjustment}
												onCancel={handleCancelCostForm}
												onLivePreview={setLiveCostPreview}
												initialData={editingCostAdjustment}
												usedTransactionIds={usedCostTransactionIds}
											/>
										)}
									{editingCostAdjustment?.id === adjustment.id &&
										activeCostForm === "add-scenario-transaction" && (
											<AddScenarioCostTransactionForm
												key={`edit-${adjustment.id}`}
												onSave={handleSaveCostAdjustment}
												onCancel={handleCancelCostForm}
												onLivePreview={setLiveCostPreview}
												initialData={editingCostAdjustment}
											/>
										)}
								</React.Fragment>
							))}

							{/* Render add form (only when not editing an existing adjustment) */}
							{!editingCostAdjustment &&
								activeCostForm === "adjust-percent" && (
									<AdjustCostsByPercentForm
										onSave={handleSaveCostAdjustment}
										onCancel={handleCancelCostForm}
										onLivePreview={setLiveCostPreview}
									/>
								)}
							{!editingCostAdjustment &&
								activeCostForm === "adjust-transaction" && (
									<AdjustSpecificCostTransactionForm
										onSave={handleSaveCostAdjustment}
										onCancel={handleCancelCostForm}
										onLivePreview={setLiveCostPreview}
										usedTransactionIds={usedCostTransactionIds}
									/>
								)}
							{!editingCostAdjustment &&
								activeCostForm === "add-scenario-transaction" && (
									<AddScenarioCostTransactionForm
										onSave={handleSaveCostAdjustment}
										onCancel={handleCancelCostForm}
										onLivePreview={setLiveCostPreview}
									/>
								)}
						</CollapsibleGroup>
					</div>
					{/* end space-y-3 collapsible groups */}
				</div>
				{/* end space-y-4 builder groups */}

				{/* RISK / CONFIDENCE / SUMMARY ROW */}
				<ScenarioImpactPreview
					mode="sidebar"
					scenarioId={scenarioId}
					adjustments={{
						revenue: revenueAdjustments,
						cost: costAdjustments,
					}}
					livePreview={{
						revenue: liveRevenuePreview,
						cost: liveCostPreview,
					}}
					scenarioChanges={
						normalizedChanges.length > 0
							? normalizedChanges
							: (scenariosRef.current.find((s) => s.id === scenarioId)
									?.changes ?? [])
					}
				/>

				{/* FULL-WIDTH CHART — uses the real ForecastTimeline pre-seeded with this scenario */}
				<ScenarioImpactPreview
					mode="chart-only"
					scenarioId={scenarioId}
					adjustments={{
						revenue: revenueAdjustments,
						cost: costAdjustments,
					}}
					livePreview={{
						revenue: liveRevenuePreview,
						cost: liveCostPreview,
					}}
					scenarioChanges={
						normalizedChanges.length > 0
							? normalizedChanges
							: (scenariosRef.current.find((s) => s.id === scenarioId)
									?.changes ?? [])
					}
					onViewTransactions={(date) => setTxnDateFilter(date)}
				/>

				{/* SCENARIO TRANSACTIONS — mirrors the 90-day Forecast Transactions
			    table, sourced from this scenario and linked to the chart above. */}
				<ScenarioTransactionsTable
					scenarioId={scenarioId}
					dateFilter={txnDateFilter}
					refreshSignal={(refreshSignal ?? 0) + adjustmentRefreshKey}
				/>
			</div>

			{/* Delete Confirmation Dialog */}
			<AlertDialog
				open={!!pendingDeleteItem}
				onOpenChange={(open) => {
					if (!open) setPendingDeleteItem(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete adjustment?</AlertDialogTitle>
						<AlertDialogDescription className="text-neutral-900">
							Are you sure you want to delete{" "}
							<strong>"{pendingDeleteItem?.name}"</strong>? This cannot be
							undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={confirmDelete}>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
